import Table, { TableRow } from 'components/ui/Table';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { returnSelectors } from 'store/reducers/returnSlice';
import { ReturnOrderLine } from 'returnCenter-api';
import { HandleReturnItemChange } from 'components/Orders/Order';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';
import CommentCell from 'components/TableCells/CommentCell';
import ReturnsOnThisLineCell from 'components/TableCells/ReturnsOnThisLineCell';
import { selectOrigin } from 'store/reducers/navigationSlice';
import InvoiceNumberCell from './Cells/InvoiceNumberCell';
import ChargeAmountCell from './Cells/ChargeAmountCell';
import CreditToggleCell from './Cells/CreditToggleCell';
import CreditPercentCell from './Cells/CreditPercentCell';
import CreditAmountCell from './Cells/CreditAmountCell';
import Flex from 'components/ui/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import styled from 'styled-components';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';

type InvoiceChargesTableProps = {
    orderNumber: string;
    disabled?: boolean;
    handleReturnItemChange: HandleReturnItemChange;
    // cellInputRefs?: MutableRefObject<{
    //     [key: string]: HTMLInputElement | HTMLSelectElement | HTMLButtonElement | HTMLDivElement | null;
    // }>;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: any, itemId: any, rowIndex: any, colIndex: any) => void;
};

const InvoiceChargesTable = ({
    orderNumber,
    disabled,
    handleReturnItemChange,
    cellInputRefs: externalCellInputRefs,
    handleArrowKeyDown,
}: InvoiceChargesTableProps) => {
    const { t } = useTranslation();
    const origin = useAppSelector(selectOrigin);
    const allInvoiceChargeLines = useAppSelector(returnSelectors.selectInvoiceChargeLines);
    const customerCard = useAppSelector(returnSelectors.selectCustomerCard);
    const currency = customerCard?.currency ?? null;

    const localCellInputRefs = useRef<
        Record<string, HTMLInputElement | HTMLSelectElement | HTMLButtonElement | HTMLDivElement | null>
    >({});
    const cellInputRefs = externalCellInputRefs ?? localCellInputRefs;

    const invoiceChargeLines = useMemo(() => allInvoiceChargeLines.filter((line) => line.orderNumber === orderNumber), [
        allInvoiceChargeLines,
        orderNumber,
    ]);

    const [expanded, setExpanded] = useState(invoiceChargeLines.length > 0);

    const isAlreadyCredited = (line: ReturnOrderLine) => (line.quantityAvailableForReturn ?? 0) <= 0;
    const getPercent = (line: ReturnOrderLine) => line.returnItems?.[0]?.percent ?? 1.0;
    const isIncluded = (line: ReturnOrderLine) => (line.returnItems?.[0]?.quantityInReturn ?? 0) > 0;
    const getItemId = (line: ReturnOrderLine) => {
        const ri = line.returnItems?.[0] as ExtendedReturnItem | undefined;
        return ri?.splitId ?? ri?.id ?? '';
    };

    const cellRefPrefix = `invoiceCharges-${orderNumber}-`;

    const handleKeyDown = useCallback(
        (e: any, itemId: any, rowIndex: any, colIndex: any) => {
            handleArrowKeyDown?.(e, itemId, rowIndex, colIndex);
        },
        [handleArrowKeyDown],
    );

    const rows = useMemo((): TableRow[] => {
        return invoiceChargeLines.map((line, rowIndex) => {
            const percent = getPercent(line);
            const amount = line.amount ?? 0;
            const included = isIncluded(line);
            const creditAmount = included ? amount * percent : 0;
            const credited = isAlreadyCredited(line);
            const itemId = getItemId(line);
            const orderLineNumber = line.orderLineNumber ?? '';
            const returnItem = line.returnItems?.[0];

            const isLocked = disabled || credited;
            const canEdit = !isLocked && included;

            return {
                key: `invoice-charge-${line.invoiceNumber}`,
                faded: credited,
                cells: [
                    {
                        span: 3,
                        el: credited ? (
                            <Text fontSize={13} italic color="textFaded">
                                {t('credited')}
                            </Text>
                        ) : (
                            <CreditToggleCell
                                checked={included}
                                disabled={isLocked}
                                skuId={orderLineNumber}
                                onToggle={(checked) => {
                                    handleReturnItemChange(
                                        orderNumber,
                                        '',
                                        orderLineNumber,
                                        itemId,
                                        'quantityInReturn',
                                        checked ? 1 : 0,
                                    );
                                }}
                                rowIndex={rowIndex}
                                colIndex={2}
                                cellRefPrefix={cellRefPrefix}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleKeyDown}
                                itemId={itemId}
                            />
                        ),
                    },
                    {
                        el: <InvoiceNumberCell canEdit={canEdit} invoiceNumber={line.invoiceNumber} />,
                    },
                    {
                        el: <ChargeAmountCell amount={amount} canEdit={canEdit} currency={currency} />,
                    },
                    {
                        el: credited ? null : (
                            <CreditPercentCell
                                percent={percent}
                                disabled={!canEdit}
                                onChange={(value) => {
                                    handleReturnItemChange(orderNumber, '', orderLineNumber, itemId, 'percent', value);
                                }}
                                rowIndex={rowIndex}
                                colIndex={3}
                                cellRefPrefix={cellRefPrefix}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleKeyDown}
                                itemId={itemId}
                            />
                        ),
                    },
                    {
                        el: <CreditAmountCell creditAmount={creditAmount} canEdit={canEdit} currency={currency} />,
                    },
                    {
                        el: returnItem ? (
                            <CommentCell
                                item={returnItem}
                                itemId={itemId}
                                orderline={line}
                                handleReturnItemChange={handleReturnItemChange}
                                disabled={!canEdit}
                                rowIndex={rowIndex}
                                colIndex={5}
                                cellInputRefs={cellInputRefs}
                                handleArrowKeyDown={handleKeyDown}
                                cellRefPrefix={cellRefPrefix}
                            />
                        ) : null,
                    },
                    {
                        el: <ReturnsOnThisLineCell returns={line.returnsOnThisLine ?? []} origin={origin} />,
                    },
                ],
            };
        });
    }, [
        invoiceChargeLines,
        disabled,
        handleReturnItemChange,
        t,
        orderNumber,
        cellRefPrefix,
        handleKeyDown,
        cellInputRefs,
        origin,
        currency,
    ]);

    if (invoiceChargeLines.length === 0) {
        return null;
    }

    const tableHeaders = [
        t('credit'),
        '',
        '',
        t('invoiceNumber'),
        t('chargeAmount'),
        t('creditPercent'),
        t('creditAmount'),
        t('notes'),
        t('returnsOnThisLine.column', 'Returns on this line'),
    ];

    return (
        <Flex column>
            <Toggle onClick={() => setExpanded((prev) => !prev)}>
                <Padding justify="between" align="center">
                    <Text as="label" bold fontSize={14}>
                        {t('invoiceCharges')} <FontAwesomeIcon icon={faFileInvoice} style={{ marginLeft: 4 }} />
                    </Text>

                    <Chevron icon={faChevronDown} $open={expanded} />
                </Padding>
            </Toggle>

            {expanded && (
                <Padding>
                    <Table
                        className="invoice-charges-table f3-300"
                        bgColor="fillWhite"
                        cols={tableHeaders.length}
                        headers={tableHeaders}
                        rows={rows}
                    />
                </Padding>
            )}
        </Flex>
    );
};

export default InvoiceChargesTable;

const Toggle = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillLight};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 0.8rem 0.45rem;
    margin-bottom: 8px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text};
`;

const Chevron = styled(FontAwesomeIcon)<{ $open: boolean }>`
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Padding = styled(Flex)`
    flex: 1;
    width: 100%;
    padding: 0 0.5rem;
`;
